import type { RouteDenialReason } from "../auth/route-access";
import { isTillOnlyRole } from "../auth/roles";
import type { WorkspaceSession } from "../auth/session";
import { landingPathFor } from "./route-manifest";

/**
 * What the workspace entry point at `/` does for the current session.
 *
 * This lives apart from the screen that draws it because two places need the
 * same answer: `WorkspaceEntryScreen`, which renders it, and the shell, which
 * has to name the resulting screen in the document title and the live
 * announcement. When those two derived the answer separately, the shell
 * announced "Workspace" to a viewer who was actually being refused one. A
 * single function is the reason that cannot happen again.
 *
 * Like every other routing decision in this application, this is a drawing
 * decision and not authorization. See `src/auth/RouteAccessGate.tsx`.
 */
export type WorkspaceEntry =
  | { kind: "pending" }
  | { kind: "redirect"; path: string }
  | { kind: "denied"; reason: RouteDenialReason };

export function resolveWorkspaceEntry(session: WorkspaceSession): WorkspaceEntry {
  switch (session.kind) {
    case "checking":
    case "unavailable":
      return { kind: "pending" };
    case "signed-out":
    case "idle-locked":
      return { kind: "redirect", path: "/sign-in" };
    case "signed-in":
      break;
  }

  const role = session.role;
  if (role === null) {
    return { kind: "denied", reason: "no-shop" };
  }
  if (isTillOnlyRole(role)) {
    return { kind: "denied", reason: "till-only-role" };
  }
  const landing = landingPathFor(role);
  if (landing === null) {
    return { kind: "denied", reason: "role-not-permitted" };
  }
  return { kind: "redirect", path: landing };
}
